import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, AlertTriangle, Info, RefreshCw, Loader2, ArrowRight, ShieldAlert } from "lucide-react";
import { api } from "@/lib/api";

// Small helpers ---------------------------------------------------------
const STATUS_META = {
  coherent: {
    label: "Coerente",
    hint: "Nessuna incoerenza rilevata. Il report può essere generato.",
    accent: "border-forensic-green bg-forensic-green/5 text-forensic-green",
    Icon: CheckCircle2,
  },
  coherent_with_warnings: {
    label: "Coerente con elementi da verificare",
    hint: "Non risultano incompatibilità bloccanti. Alcuni elementi meritano verifica o approfondimento.",
    accent: "border-forensic-amber bg-forensic-amber/10 text-forensic-amber",
    Icon: AlertTriangle,
  },
  inconsistent: {
    label: "Incoerenze da risolvere",
    hint: "Sono presenti informazioni incompatibili che potrebbero rendere il report fuorviante. Correggile prima di procedere.",
    accent: "border-forensic-red bg-forensic-red/5 text-forensic-red",
    Icon: ShieldAlert,
  },
};

const SEVERITY_META = {
  blocking: { l: "Bloccante", accent: "border-forensic-red bg-forensic-red/5 text-forensic-red" },
  warning: { l: "Da verificare", accent: "border-forensic-amber bg-forensic-amber/10 text-forensic-amber" },
  informational: { l: "Informativo", accent: "border-slate-400 bg-slate-100 text-slate-600" },
};

const CATEGORY_LABELS = {
  document: "Documento",
  comparative: "Comparativa",
  temporal: "Cronologia",
  clinical: "Clinico",
  assessment: "Valutazione",
  recommendation: "Raccomandazione",
  multidocument: "Multidocumento",
  narrative: "Report",
};

// Map every consistency related_field to a Wizard step (visible label).
function resolveWizardStep(field) {
  const f = (field || "").toLowerCase();
  if (!f) return { step: 2, label: "Documento" };
  if (f.startsWith("comparatives")) return { step: 3, label: "Comparative" };
  if (f.startsWith("timeline") || f.includes("temporal") || f.includes("comparative_relations")) {
    return { step: 4, label: "Cronologia" };
  }
  if (f.startsWith("clinical")) return { step: 5, label: "Strategia" };
  if (f.startsWith("recommendations") || f === "narrative" || f === "report_context") {
    return { step: 6, label: "Conferma" };
  }
  return { step: 2, label: "Documento" };
}

export default function PremiumFinalCheck({ caseId, onStatusChange }) {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/cases/${caseId}/premium/precheck`);
      setData(r.data);
      setError(null);
      onStatusChange && onStatusChange(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Impossibile eseguire il controllo di coerenza");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (caseId) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId]);

  if (loading && !data) {
    return (
      <section className="mt-6 border hairline bg-white p-6" data-testid="c2-final-check-loading">
        <div className="flex items-center gap-2 text-slate-600 text-sm">
          <Loader2 size={14} className="animate-spin" /> Controllo di coerenza in corso…
        </div>
      </section>
    );
  }
  if (error) {
    return (
      <section className="mt-6 border hairline bg-white p-6" data-testid="c2-final-check-error">
        <p className="text-sm text-forensic-red">{error}</p>
        <button onClick={load} className="mt-3 inline-flex items-center gap-2 text-xs text-navy hover:underline"
          data-testid="c2-retry-btn">
          <RefreshCw size={12} /> Riprova
        </button>
      </section>
    );
  }
  if (!data) return null;

  const status = data.consistency_status;
  const meta = STATUS_META[status] || STATUS_META.coherent;
  const counts = data.counts || { blocking: 0, warnings: 0, informational: 0 };
  const StatusIcon = meta.Icon;

  return (
    <section
      className="mt-6 border-2 border-navy bg-white p-6 lg:p-7"
      data-testid="c2-final-check"
      data-consistency-status={status}>
      <div className="overline text-navy mb-1 flex items-center gap-2">
        <ShieldAlert size={12} /> Controllo finale del fascicolo
      </div>
      <p className="text-xs text-slate-600 leading-snug mb-4 max-w-3xl">
        Perizia Ready verifica automaticamente la coerenza delle informazioni dichiarate prima
        della generazione del report. Questo controllo è distinto dagli alert clinici e temporali
        del report (elementi da approfondire).
      </p>

      {/* Status banner */}
      <div className={`border hairline p-4 mb-5 ${meta.accent}`} data-testid="c2-status-banner">
        <div className="flex items-start gap-3">
          <StatusIcon size={18} className="mt-0.5 shrink-0" />
          <div>
            <div className="font-serif text-lg font-semibold leading-tight">{meta.label}</div>
            <p className="text-xs mt-1 text-slate-700 leading-snug">{meta.hint}</p>
            {data.is_stale && (
              <p className="text-[11px] mt-2 text-forensic-amber flex items-center gap-1"
                data-testid="c2-stale-hint">
                <RefreshCw size={11} /> Controllo di coerenza da aggiornare: il fascicolo è stato modificato dopo l'ultima verifica.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Counts */}
      <div className="grid grid-cols-3 gap-3 mb-5" data-testid="c2-counts">
        <div className="border hairline p-3 bg-paper">
          <div className="overline text-forensic-red">Bloccanti</div>
          <div className="font-serif text-3xl font-semibold text-forensic-red mt-1"
            data-testid="c2-count-blocking">{counts.blocking || 0}</div>
        </div>
        <div className="border hairline p-3 bg-paper">
          <div className="overline text-forensic-amber">Da verificare</div>
          <div className="font-serif text-3xl font-semibold text-forensic-amber mt-1"
            data-testid="c2-count-warnings">{counts.warnings || 0}</div>
        </div>
        <div className="border hairline p-3 bg-paper">
          <div className="overline text-slate-500">Informativi</div>
          <div className="font-serif text-3xl font-semibold text-slate-600 mt-1"
            data-testid="c2-count-info">{counts.informational || 0}</div>
        </div>
      </div>

      {/* Issues */}
      <IssueList issues={data.blocking_issues} severity="blocking" caseId={caseId} nav={nav} />
      <IssueList issues={data.warnings} severity="warning" caseId={caseId} nav={nav} />
      <IssueList issues={data.informational_notes} severity="informational" caseId={caseId} nav={nav} />

      {/* Refresh */}
      <div className="mt-4 pt-4 border-t hairline flex items-center justify-between gap-3 flex-wrap">
        <p className="text-[11px] text-slate-500 leading-snug max-w-lg">
          Il controllo di coerenza viene ricalcolato automaticamente ogni volta che apri questa
          pagina o generi il report. Le issue risolte scompaiono senza intervento manuale.
        </p>
        <button onClick={load} disabled={loading}
          className="inline-flex items-center gap-2 border border-navy text-navy hover:bg-navy hover:text-white px-3 py-2 rounded-sm text-xs transition-colors disabled:opacity-60"
          data-testid="c2-refresh-btn">
          {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
          Aggiorna controllo
        </button>
      </div>
    </section>
  );
}

function IssueList({ issues, severity, caseId, nav }) {
  if (!issues || issues.length === 0) return null;
  const meta = SEVERITY_META[severity] || SEVERITY_META.informational;
  const heading = severity === "blocking"
    ? "Incoerenze bloccanti"
    : severity === "warning"
      ? "Elementi da verificare"
      : "Note informative";
  return (
    <div className="mb-4" data-testid={`c2-issues-${severity}`}>
      <div className="overline mb-2">{heading} · {issues.length}</div>
      <div className="space-y-2">
        {issues.map((iss, i) => {
          const cat = CATEGORY_LABELS[iss.category] || iss.category;
          const docLabel = (iss.related_document_ids && iss.related_document_ids.length > 0)
            ? iss.related_document_ids.map((d) => {
                const m = /doc-(\d+)/.exec(d);
                return m ? `DOC #${String(parseInt(m[1]) + 1).padStart(2, "0")}` : d;
              }).join(", ")
            : null;
          const target = resolveWizardStep(iss.related_field);
          return (
            <div key={`${iss.rule_id}-${i}`}
              className={`border-l-2 border hairline p-3 ${meta.accent}`}
              data-testid={`c2-issue-${iss.rule_id}`}>
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <div className="text-[11px] uppercase tracking-wider font-mono text-slate-500">
                  <span className={`inline-block mr-2 px-2 py-0.5 rounded-sm text-[10px] ${meta.accent}`}>
                    {meta.l}
                  </span>
                  {cat}{docLabel ? ` — ${docLabel}` : ""}
                </div>
                <span className="text-[10px] font-mono text-slate-400">{iss.rule_id}</span>
              </div>
              <p className="text-sm text-slate-800 leading-snug mt-2">{iss.message}</p>
              {iss.suggested_action && (
                <p className="text-xs italic text-slate-600 mt-1 leading-snug">
                  Azione consigliata: {iss.suggested_action}
                </p>
              )}
              {(iss.related_document_ids?.length > 0 || iss.related_field) && (
                <button
                  type="button"
                  onClick={() => nav(`/wizard?case=${caseId}&step=${target.step - 1}`)}
                  className="inline-flex items-center gap-1 mt-2 text-[11px] text-navy hover:underline"
                  data-testid={`c2-goto-${iss.rule_id}`}>
                  Vai al campo <ArrowRight size={11} />
                  <span className="text-slate-400 ml-1">(Step {target.step} · {target.label})</span>
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Named helper export for hosts that want to use the same status-to-can-generate rule.
export function canGenerate(consistency) {
  if (!consistency) return true;
  return consistency.consistency_status !== "inconsistent";
}

// Small badge to render in dashboards / lists.
export function ConsistencyBadge({ status }) {
  const meta = STATUS_META[status];
  if (!meta) return null;
  const { Icon } = meta;
  return (
    <span className={`inline-flex items-center gap-1 border hairline px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-wider ${meta.accent}`}
      data-testid="consistency-badge">
      <Icon size={10} />
      {meta.label}
    </span>
  );
}

export { STATUS_META, SEVERITY_META, CATEGORY_LABELS, Info };
